import React from "react";
import { api } from "@/lib/api";
import { SafeHTML } from "@/components/site/RichTextEditor";

// Module-level cache so we don't refetch the same slug on every component instance
const cache = new Map(); // slug -> Promise<Block[]>

function fetchBlocks(slug) {
  if (!cache.has(slug)) {
    cache.set(
      slug,
      api.get("/page-blocks", { params: { page_slug: slug } })
        .then((r) => r.data || [])
        .catch(() => [])
    );
  }
  return cache.get(slug);
}

// Allow admins to force a refresh after editing a block in another tab
export function invalidateEditableTextCache(slug) {
  if (slug) cache.delete(slug);
  else cache.clear();
}

/**
 * Inline, admin-editable text zone.
 * - Looks up a PageBlock with matching `slug` + `sectionKey`.
 * - If found and active → renders its rich-text HTML (via SafeHTML).
 * - Otherwise → renders the `children` (default hardcoded fallback).
 *
 * Admins edit this from "📝 Textes & blocs de page" by creating a block
 * with the exact same section_key.
 */
export default function EditableText({ slug, sectionKey, children, className = "", as: Tag = "div" }) {
  const [block, setBlock] = React.useState(undefined);

  React.useEffect(() => {
    let alive = true;
    fetchBlocks(slug).then((blocks) => {
      if (!alive) return;
      const match = blocks.find((b) => b.section_key === sectionKey && b.active !== false);
      setBlock(match || null);
    });
    return () => { alive = false; };
  }, [slug, sectionKey]);

  // While loading, render fallback (avoids flicker / empty space)
  if (block === undefined || block === null) {
    return <Tag className={className} data-editable-slug={slug} data-editable-key={sectionKey}>{children}</Tag>;
  }
  return (
    <Tag className={className} data-editable-slug={slug} data-editable-key={sectionKey} data-editable-block={block.id}>
      <SafeHTML html={block.html} />
    </Tag>
  );
}
